import { useCallback, useMemo, useState } from 'react';
import type { LabMarkerTVitalLabMarkerMetadataT as LabMarkerT } from '@lotushealth/lotushealth_js_sdk/schemas';

export interface AddMarkerResult {
  ok: boolean;
  reason?: 'duplicate' | 'different_lab' | 'unknown_lab';
  selectedLabId: string | null;
  markerLabId: string | null;
}

function getLabId(marker: LabMarkerT): string | null {
  return marker.metadata.lab_info?.lab_id ?? null;
}

function firstNonEmptyLabId(markers: ReadonlyArray<LabMarkerT>): string | null {
  for (const m of markers) {
    const id = getLabId(m);
    if (typeof id === 'string' && id.trim() !== '') {
      return id;
    }
  }
  return null;
}

export interface MarkerSelectionModel {
  selectedMarkers: LabMarkerT[];
  selectedLabId: string | null;
  addMarker: (marker: LabMarkerT) => AddMarkerResult;
  removeMarker: (markerId: string) => void;
  clear: () => void;
}

/**
 * Client-side marker selection state with same-lab enforcement.
 *
 * Notes:
 * - The first marker with a known lab_id sets `selectedLabId`.
 * - Once `selectedLabId` is set, any subsequent marker must match it.
 */
export function useMarkerSelection(): MarkerSelectionModel {
  const [selectedMarkers, setSelectedMarkers] = useState<LabMarkerT[]>([]);
  const [selectedLabId, setSelectedLabId] = useState<string | null>(null);

  const addMarker = useCallback(
    (marker: LabMarkerT): AddMarkerResult => {
      const markerLabId = getLabId(marker);

      // Do not add duplicates.
      if (selectedMarkers.some((m) => m.id === marker.id)) {
        return { ok: false, reason: 'duplicate', selectedLabId, markerLabId };
      }

      // If we already have a sticky lab id, enforce it.
      if (selectedLabId !== null) {
        if (markerLabId === null) {
          return { ok: false, reason: 'unknown_lab', selectedLabId, markerLabId };
        }
        if (markerLabId !== selectedLabId) {
          return { ok: false, reason: 'different_lab', selectedLabId, markerLabId };
        }
      }

      // If we don't yet have a sticky lab id, adopt the first known one.
      if (selectedLabId === null && markerLabId !== null) {
        setSelectedLabId(markerLabId);
      }

      setSelectedMarkers((prev) => [...prev, marker]);
      return { ok: true, selectedLabId: selectedLabId ?? markerLabId, markerLabId };
    },
    [selectedLabId, selectedMarkers]
  );

  const removeMarker = useCallback((markerId: string) => {
    setSelectedMarkers((prev) => {
      const next = prev.filter((m) => m.id !== markerId);
      setSelectedLabId(next.length ? firstNonEmptyLabId(next) : null);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSelectedMarkers([]);
    setSelectedLabId(null);
  }, []);

  return useMemo(
    () => ({
      selectedMarkers,
      selectedLabId,
      addMarker,
      removeMarker,
      clear,
    }),
    [addMarker, clear, removeMarker, selectedLabId, selectedMarkers]
  );
}
